import { Checkbox, Col, Image, Row } from "antd"
import Link from "next/link"
import HoverIconSwitch from "@/components/checkout/HoverIconSwitch"
import AddOnDealRow from "@/components/view-cart/AddOnDealRow"
import CartQuantitySelect from "@/components/view-cart/CartQuantitySelect"
import DeleteHover from '@/svgs/checkout/delete-hover.svg'
import DeleteIcon from '@/svgs/checkout/delete.svg'
import WishListHover from '@/svgs/checkout/My-Wishlist-hover.svg'
import WishListIcon from '@/svgs/checkout/My-Wishlist.svg'
import InfoSvg from '@/svgs/icon-Info.svg'
import { formatCurrency, getCompressedImageUrl } from "@/utils/utils"
import styles from "@/styles/view-cart.module.scss"

interface CartColumnsParams {
  disabled: boolean
  loadingRows: Record<string | number, boolean>
  onDeleteItem: (_record: any) => void
  onAddonInfoClick?: () => void
  onExpressChange: (_record: any, _event: any) => void
  onQtyChange: (_record: any, _value: string | number) => void
  onWishlistItem: (_record: any) => void
}

export const createCartColumns = ({
  disabled,
  loadingRows,
  onDeleteItem,
  onAddonInfoClick,
  onExpressChange,
  onQtyChange,
  onWishlistItem
}: CartColumnsParams): any[] => {
  const nameTd = {
    width: "380px",
    title: 'Select all items',
    dataIndex: 'productName',
    onCell: (record: any) => {
      if (record.isAddonTitle) return { colSpan: 7 }
      if (record.isAddonColHead) return { colSpan: 2 }
      return {}
    },
    render: (name: any, record: any) => (
      record.isAddonDivider ? null :
      record.isAddonTitle ? (
        <div className={styles['addon-title-row']}>
          <span className={styles['addon-header-title']} onClick={onAddonInfoClick}>Super Value Add-on Deals</span>
          <InfoSvg style={{ width: 16, marginLeft: 6 }} onClick={onAddonInfoClick} />
        </div>
      ) : record.isAddonColHead ? null :
      record.isAddonRow ? (
        <AddOnDealRow record={record} status={record.addonStatus || 'normal'} />
      ) : (
        <div className={`${styles['goods-box']}`}>
          <Link href={record.url} legacyBehavior>
            <a rel="noopener noreferrer" className={`${styles['goodsImg']} ${disabled && styles['disabled']}`}>
              <Image
                alt={name || ''}
                src={getCompressedImageUrl(record.defaultFullUrl, 112)}
                width="100%"
                height="100%"
                preview={false}
              />
            </a>
          </Link>
          <div className={styles['goodsInfo']}>
            <Link href={record.url} legacyBehavior>
              <a rel="noopener noreferrer" className={styles['name']}>{name}</a>
            </Link>
            <div className={styles['pid']}>PID: {record.productID}</div>
          </div>
        </div>
      )
    )
  }

  const quantityTd = {
    title: 'Quantity',
    dataIndex: 'quantity',
    align: "center",
    width: "130px",
    onCell: (record: any) => {
      if (record.isAddonTitle || record.isAddonColHead) return { colSpan: 0 }
      return {}
    },
    render: (qty: string, record: any) => (
      record.isAddonDivider ? null :
      record.isAddonRow ? (
        record.addonStatus === 'locked' ? null : <strong>&times;{qty || 1}</strong>
      ) :
      <CartQuantitySelect
        disabled={disabled}
        quantity={disabled ? 1 : parseInt(qty)}
        maxNumber={parseInt(record.inStock > 50 ? 50 : record.inStock)}
        moreThen={record.inStock > 50}
        qtyChange={onQtyChange.bind(qty, record)}
      />
    )
  }

  const renderAddonPrice = (price: string | number, record: any) => {
    const currentPrice = Number(price)
    const normalPrice = Number(record.beforeAddonPrice)
    const savePrice = normalPrice - currentPrice

    if (normalPrice > currentPrice && savePrice > 0) {
      return (
        <>
          <div>
            <strong className={styles['postage-price']}>{formatCurrency(currentPrice)}</strong>
            <span className={styles['postage-cross-price']}>{formatCurrency(normalPrice)}</span>
          </div>
          <div className={styles['postage-save-price']}>Save: {formatCurrency(savePrice)}</div>
        </>
      )
    }

    return <strong>{formatCurrency(price)}</strong>
  }

  const priceTd = {
    title: 'Unit Price',
    dataIndex: 'price',
    align: "center",
    width: "130px",
    onCell: (record: any) => {
      if (record.isAddonTitle) return { colSpan: 0 }
      return {}
    },
    render: (price: string | number, record: any) => (
      record.isAddonDivider ? null :
      record.isAddonColHead ? (
        <span className={styles['addon-col-header']}>Add-on Price</span>
      ) : record.isAddonRow ? (
        record.addonStatus === 'locked' ? (
          <span className={styles['locked-addon-price']}>{formatCurrency(price)}</span>
        ) : renderAddonPrice(price, record)
      ) : (
        <>{formatCurrency(price)}</>
      )
    )
  }

  const expressTd = {
    title: 'Express',
    dataIndex: 'upgrade_express',
    align: "center",
    onCell: (record: any) => {
      if (record.isAddonTitle || record.isAddonColHead || record.isAddonRow) return { colSpan: 0 }
      return {}
    },
    render: (upgrade_express: string | number, record: any) => (
      record.isAddonDivider ? null :
      record.isAddonRow ? <>-</> :
      disabled ? (
        <>-</>
      ) : (record.appSelectStatus == 1 && record.isShowUpgrade == 1)
        ?
        <Row justify="center" align="middle" wrap={true} style={{ textAlign: "center" }}>
          <Col span={24}>
            <Checkbox checked={record.upgradeExpress == 1} onChange={onExpressChange.bind(upgrade_express, record)}></Checkbox>
          </Col>
          <Col span={24}>
            <div>upgrade to<br />express post</div>
          </Col>
        </Row>
        :
        <>-</>
    )
  }

  const renderPostage = (postage: string | number, record: any) => (
    record.savePostage > 0 ? (
      <>
        <div>
          <span className={styles['postage-price']}>{formatCurrency(postage)}</span>
          <span className={styles['postage-cross-price']}>{formatCurrency(record.normalPostage)}</span>
        </div>
        <div className={styles['postage-save-price']}>Save: {formatCurrency(record.savePostage)}</div>
      </>
    ) : (
      <> {postage == -1 ? 'Undeliverable' : `${formatCurrency(postage)}`}</>
    )
  )

  const postageTd = {
    width: "115px",
    title: 'Postage',
    dataIndex: 'postage',
    align: "center",
    onCell: (record: any) => {
      if (record.isAddonTitle) return { colSpan: 0 }
      if (record.isAddonColHead || record.isAddonRow) return { colSpan: 2 }
      return {}
    },
    render: (postage: string | number, record: any) => (
      record.isAddonDivider ? null :
      record.isAddonColHead ? (
        <span className={styles['addon-col-header']}>Postage</span>
      ) : record.isAddonRow ? (
        record.addonStatus === 'locked' ? (
          <span className={styles['locked-addon-price']}>{formatCurrency(postage)}</span>
        ) : loadingRows[record.productID] ? <>loading</> : renderPostage(postage, record)
      ) :
      disabled ? (
        <>-</>
      ) :
        loadingRows[record.productID] ? (
          <>loading</>
        ) :
          renderPostage(postage, record)
    )
  }

  const subtotalTd = {
    title: 'Item Total',
    dataIndex: 'itemTotal',
    align: "center",
    width: "120px",
    onCell: (record: any) => {
      if (record.isAddonTitle) return { colSpan: 0 }
      return {}
    },
    render: (itemTotal: string | number, record: any) => (
      record.isAddonDivider ? null :
      record.isAddonColHead ? (
        <span className={styles['addon-col-header']}>Item Total</span>
      ) : record.isAddonRow ? (
        record.addonStatus === 'locked' ? (
          <strong className={styles['locked-addon-total']}>Locked</strong>
        ) : <strong>{formatCurrency(parseFloat(`${itemTotal}`).toFixed(2))}</strong>
      ) :
      disabled ? (
        <>-</>
      ) :
        loadingRows[record.productID] ? (
          <>loading</>
        ) : (
          <strong>{formatCurrency(parseFloat(`${itemTotal}`).toFixed(2))}</strong>
        )
    )
  }

  const configTd = {
    width: "120px",
    title: '',
    dataIndex: 'productID',
    onCell: (record: any) => {
      if (record.isAddonTitle) return { colSpan: 0 }
      return {}
    },
    render: (productID: any, record: any) => {
      if (record.isAddonDivider) return null
      if (record.isAddonRow) {
        if (record.addonStatus === 'locked') {
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <HoverIconSwitch
                IconOne={<DeleteIcon className={styles['cart-icon']} onClick={onDeleteItem.bind(productID, record)} />}
                IconTwo={<DeleteHover className={styles['cart-icon']} onClick={onDeleteItem.bind(productID, record)} />}
                hoverContent="Delete"
              />
            </div>
          )
        }
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <HoverIconSwitch
              IconOne={<DeleteIcon className={styles['cart-icon']} onClick={onDeleteItem.bind(productID, record)} />}
              IconTwo={<DeleteHover className={styles['cart-icon']} onClick={onDeleteItem.bind(productID, record)} />}
              hoverContent="Delete"
            />
          </div>
        )
      }
      if (record.isAddonColHead) return null
      return (
        <>
          <HoverIconSwitch
            IconOne={<DeleteIcon className={styles['cart-icon']} onClick={onDeleteItem.bind(productID, record)} />}
            IconTwo={<DeleteHover className={styles['cart-icon']} onClick={onDeleteItem.bind(productID, record)} />}
            hoverContent="Delete"
          />
          <HoverIconSwitch
            IconOne={<WishListIcon className={styles['cart-icon']} onClick={onWishlistItem.bind(productID, record)} />}
            IconTwo={<WishListHover className={styles['cart-icon']} onClick={onWishlistItem.bind(productID, record)} />}
            hoverContent="Move to wishlist"
          />
        </>
      )
    }
  }

  return [
    nameTd,
    quantityTd,
    priceTd,
    expressTd,
    postageTd,
    subtotalTd,
    configTd
  ]
}
